import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import AlphaplanIntegrationForm from './AlphaplanIntegrationForm.tsx';
import { buildAlphaplanMutationVariables } from './mutationVariables.ts';
import { GET_ALPHAPLAN_INTEGRATIONS, UPDATE_ALPHAPLAN_INTEGRATION } from './queries.ts';
import type { AlphaplanFormData, AlphaplanIntegration } from './types.ts';
import Button from '../../../core/components/Button.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_ALPHAPLAN_PATH } from '../../constants.ts';

const FORM_ID = 'alphaplan-integration-form';

const AlphaplanIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const { publishToast } = useToaster();

    const backTooltipText = `${t('backTooltipText')} ${t('alphaplan.title')}`;

    const { data, loading, refetch } = useQuery(GET_ALPHAPLAN_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration: AlphaplanIntegration | undefined = data?.alphaplanIntegrations?.find(
        (i) => i.id === integrationId
    );

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_ALPHAPLAN_INTEGRATION);

    const handleUpdateIntegration = async (formData: AlphaplanFormData) => {
        if (!integration) return;

        try {
            await updateIntegration({
                variables: { id: integration.id, ...buildAlphaplanMutationVariables(formData) },
            });
            await refetch();
            publishToast({ description: t('alphaplan.updateSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            // the backend rejects invalid arguments with a readable GraphQL error, e.g. a bad base URL
            const reason = error instanceof Error ? error.message : '';
            publishToast({
                description: reason ? `${t('alphaplan.updateError')}: ${reason}` : t('alphaplan.updateError'),
                status: 'error',
            });
        }
    };

    if (!loading && !integration) {
        return (
            <Layout>
                <SettingsNavigation />
                <Page className="flex-1">
                    <Page.Content lowered>
                        <p className="text-content-medium">{t('alphaplan.noIntegrations')}</p>
                        <Button type="button" asChild className="mt-4">
                            <Link to={url(CUSTOMER_INTEGRATION_ALPHAPLAN_PATH)}>
                                {t('alphaplan.form.cancelButton')}
                            </Link>
                        </Button>
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('alphaplan.title'), to: url(CUSTOMER_INTEGRATION_ALPHAPLAN_PATH) },
                            { label: integration?.name || t('alphaplan.form.updateTitle') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_ALPHAPLAN_PATH)}>
                                {t('alphaplan.form.cancelButton')}
                            </Link>
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            form={FORM_ID}
                            loading={isSubmitting}
                            disabled={loading || !integration}
                        >
                            {t('alphaplan.form.updateButton')}
                        </Button>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    {!loading && integration && (
                        <AlphaplanIntegrationForm
                            integration={integration}
                            onSubmit={handleUpdateIntegration}
                            formId={FORM_ID}
                        />
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default AlphaplanIntegrationEditPage;
